import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import CNRRangeTable from "./Table";
import reportActions from "redux/app/report/actions";
import reportSelectors from "redux/app/report/selectors";
import Select from "react-select";
import { vesselActions, vesselSelectors } from "redux/app/vessel";
import classNames from "classnames";
import {
	PAGE_SIZE,
	signalReportTypeOptions,
	vesselSubTypeOptions,
} from "redux/app/report/constants";
import TabPicker from "components/_common/TabPicker";
import {
	ECNR_BASE_RANGE_TYPE,
	ICNRCellInfo,
	ICNRRangesVesselsLoadParams,
	ICNRRangeVesselLocalRange,
} from "redux/app/report/@types";
import ModalPortal from "components/app/_common/ModalPortal";
import CNRRangeForm from "./CNRRangeForm";
import { ESIGNAL_REPORT_TYPE_VAL } from "redux/app/vessel/onboard/types";

const commonSelectStyles = {
	option: ({ isFocused, isSelected }: any) =>
		classNames(
			isFocused && "hover:cursor-pointer",
			isSelected && "font-medium rounded bg-gray-200",
			"p-0.5 py-1.5 hover:bg-gray-200 rounded"
		),
	dropdownIndicator: () =>
		"p-1 hover:bg-gray-100 text-gray-500 rounded-md hover:text-black",
	menu: () => "p-[3px] -mt-1 border border-gray-200 bg-white rounded-md z-50",
	placeholder: () => "text-gray-500 pl-1 py-0.5",
	valueContainer: () => "mt-0.5 px-2",
	menuList: () => "z-50",
};

type OwnProps = {};
type CNRRangesProps = PropsFromRedux & OwnProps;
function CNRRanges(props: CNRRangesProps) {
	const {
		vesselSubType,
		miniVessels,
		vesselsSelected,
		reportType,
		baseRangeType,
		isEditModalVisible,
		rangeInfo,
		pagination,
		jobToUploadCNRRangesCSVFile,
		jobToLoadCNRRangesVessels,
		jobToLoadVessels,
	} = props;

	const isFileUploadDisabled = jobToUploadCNRRangesCSVFile.isLoading;

	const vesselsSelectedStr = vesselsSelected?.map((v) => v).join(",");
	const resetScroll = () => {
		const container = document.querySelector(".cnr-ranges-table");
		if (container) {
			container.scrollTo({
				left: 0,
				behavior: "smooth",
			});
		}
	};

	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) {
			console.error("No file to upload.");
			return;
		}
		props.uploadCSVOfCNRRanges(file);
		// Reset input value to allow selecting the same file again
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleButtonClick = () => {
		fileInputRef.current?.click();
	};
	const [vesselIdOfCellToBeUpdated, setVesselIdOfCellToBeUpdated] =
		useState("");
	const [cellInfo, setCellInfo] = useState<ICNRCellInfo>({
		key: "",
		metric: "",
		type: "",
	});

	useEffect(() => {
		props.loadVessels();

		return () => {
			props.resetCNRRangeVesselList();
			props.resetColumns();
			props.resetPagination();
			props.setVesselSubType(vesselSubTypeOptions[0].value);
			props.setReportTypeSelected(ESIGNAL_REPORT_TYPE_VAL.STEAMING);
			props.setVesselsSelected([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		resetScroll();
		return () => {
			resetScroll();
		};
	}, [vesselSubType, reportType, vesselsSelectedStr]);

	useEffect(() => {
		props.resetColumns();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vesselsSelectedStr]);

	const vesselOptions = [
		...miniVessels.ids.map((id) => {
			return {
				...miniVessels.byIds[id],
				label: miniVessels.byIds[id].vesselName,
				value: miniVessels.byIds[id].id,
			};
		}),
	];

	const isModuleLoading =
		jobToLoadVessels.isLoading ||
		jobToLoadCNRRangesVessels.isLoading ||
		jobToLoadCNRRangesVessels.isLoading;

	return (
		<div className="relative">
			<div className="flex flex-col">
				<div className="bg-white m-2 rounded-lg h-[calc(100vh-98px)] overflow-y-auto px-5">
					<div className="py-8 pb-0 pt-4">
						<div className="flex flex-col md:flex-row items-center justify-between">
							<div>
								<div className="flex flex-col md:flex-row md:items-center space-x-4">
									<div className="flex items-center">
										<span
											title="go back"
											onClick={() => {
												window?.history?.back();
											}}
											className="mr-3 text-lg cursor-pointer bp3-icon bp3-icon-arrow-left"
										/>
										<h2 className="text-xl font-medium">CNR Ranges</h2>
									</div>
									<div className="flex justify-center space-x-4">
										<div className="font-medium text-gray-600 min-w-28 w-36 md:w-56">
											<Select
												key={vesselSubType}
												isSearchable
												placeholder="Select Subtype"
												unstyled
												defaultValue={vesselSubTypeOptions[0]}
												value={vesselSubTypeOptions?.find(
													(vo) => vo.value === vesselSubType
												)}
												options={vesselSubTypeOptions}
												className="text-[12px] mt-1 xl:text-sm lg:min-w-32 xl:min-w-[220px]"
												onChange={(newValue) => {
													props.setVesselSubType(newValue?.value);
													props.setVesselsSelected([]);
												}}
												classNames={{
													...commonSelectStyles,
													singleValue: () => "",
													control: () =>
														"bg-gray-100 border shadow-sm border border-gray-300 rounded-sm z-50",
												}}
												isDisabled={isModuleLoading}
											/>
										</div>

										<Select
											key={reportType}
											isSearchable
											placeholder="Select Report"
											unstyled
											value={signalReportTypeOptions?.find(
												(sro) => sro.value === reportType
											)}
											options={signalReportTypeOptions}
											className="text-[12px] mt-1 xl:text-sm lg:min-w-32 xl:min-w-[220px]"
											onChange={(newValue) => {
												props.setReportTypeSelected(newValue?.value);
											}}
											classNames={{
												...commonSelectStyles,
												menu: () =>
													"p-[3px] -mt-1 border border-gray-200 bg-white rounded-md z-40",
												control: () =>
													"bg-gray-100 border shadow-sm border border-gray-300 rounded-sm z-40",
												singleValue: () => "text-[13px] font-thin",
											}}
											isDisabled={isModuleLoading}
										/>
									</div>
								</div>
							</div>
							<div className="relative inline-block mt-4 md:mt-0">
								<input
									ref={fileInputRef}
									type="file"
									accept=".csv"
									onChange={handleFileChange}
									disabled={isFileUploadDisabled || isModuleLoading}
									className="sr-only"
									aria-label="Upload CSV file"
								/>
								<button
									onClick={handleButtonClick}
									disabled={isFileUploadDisabled}
									className={classNames(
										`inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 focus:bg-blue-600 text-blue-100 font-medium text-[12px] lg:text-sm border border-transparent focus:outline-none  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 transition-colors duration-200`,
										{
											"cursor-not-allowed":
												isFileUploadDisabled || isModuleLoading,
										}
									)}
									type="button"
									aria-label="Select CSV file to upload"
								>
									<span className="bp3-icon bp3-icon-document" />
									<span> Upload CSV</span>
								</button>
							</div>
						</div>
					</div>
					<section className="mt-2 xl:mt-4 mb-2">
						<div className="flex flex-col items-center lg:flex-row justify-center md:items-start lg:justify-between">
							<div className="pb-2 xl:pb-4 flex flex-col md:flex-row space-x-4 items-center md:w-[calc(100%-320px)]">
								<div className="flex items-center space-x-2">
									<div className="w-full">
										<Select
											isDisabled={isModuleLoading}
											key={vesselsSelectedStr}
											isMulti
											isSearchable
											isClearable
											placeholder="Select Vessel"
											unstyled
											value={vesselOptions?.filter((vo) =>
												vesselsSelected?.includes(vo.value)
											)}
											// value={vesselOptions?.find(
											// 	(vo) => vo.value === vesselSelected
											// )}
											options={vesselOptions}
											className="text-[12px] mt-1 xl:text-sm w-full lg:min-w-32 xl:min-w-[220px]"
											onChange={(newValue) => {
												const selectedVessels = newValue?.map((v) => v.value);
												props.resetCNRRangeVesselList();
												props.setVesselsSelected(selectedVessels);
												props.setVesselSubType(undefined);
												props.loadCNRRangeVessels({
													baseRangeType,
													selectedVessels,
													paginationParams: {
														...pagination,
														limit: PAGE_SIZE,
														skip: 0,
														hasMore: true,
													},
												});
											}}
											classNames={{
												...commonSelectStyles,
												multiValue: () =>
													"bg-gray-300 rounded p-0.5 m-0.5 text-[12px]",
												control: () =>
													"bg-gray-100 border shadow-sm border border-gray-300 rounded-sm z-40",
											}}
										/>
									</div>
								</div>
							</div>
							<div className="lg:self-start self-center lg:justify-start mt-1 mb-3 ml-4 lg:mb-0 md:w-[290px] lg:w-auto">
								<TabPicker
									options={[
										{
											label: "Custom Range",
											value: ECNR_BASE_RANGE_TYPE.LOCAL,
											isDisabled: false,
										},
										{
											label: "Default Range",
											value: ECNR_BASE_RANGE_TYPE.CONFIG,
											isDisabled: true,
										},
									]}
									defaultValue={baseRangeType}
									onChange={(selectedValue: string) => {
										if (selectedValue === ECNR_BASE_RANGE_TYPE.CONFIG) return;

										props.setBaseRangeType(
											selectedValue as ECNR_BASE_RANGE_TYPE
										);
										props.resetPagination();
										props.resetTable();
										props.resetColumns();
									}}
								/>
							</div>
						</div>
					</section>
					<section>
						{/* TODO: Can remove these conditionals, both will render one single component. Based on baseRangeType whether it's Local(custom) or Config(default) ranges */}
						{baseRangeType === ECNR_BASE_RANGE_TYPE.LOCAL ? (
							<CNRRangeTable
								key={baseRangeType}
								baseRangeType={props.baseRangeType}
								setRangeInfo={props.setRangeInfo}
								reportType={reportType}
								toggleEditModalVisible={props.toggleEditModalVisible}
								setCellInfo={setCellInfo}
								setVesselIdOfCellToBeUpdated={setVesselIdOfCellToBeUpdated}
								isFileUploading={jobToUploadCNRRangesCSVFile.isLoading}
							/>
						) : null}
						{baseRangeType === ECNR_BASE_RANGE_TYPE.CONFIG ? (
							<CNRRangeTable
								key={baseRangeType}
								baseRangeType={props.baseRangeType}
								setRangeInfo={props.setRangeInfo}
								reportType={reportType}
								toggleEditModalVisible={props.toggleEditModalVisible}
								setCellInfo={setCellInfo}
								setVesselIdOfCellToBeUpdated={setVesselIdOfCellToBeUpdated}
								isFileUploading={jobToUploadCNRRangesCSVFile.isLoading}
							/>
						) : null}
					</section>
					{isEditModalVisible ? (
						<ModalPortal
							onClose={() => props.toggleEditModalVisible(false)}
							isActive={isEditModalVisible}
							isOverlayClickCloseable={false}
						>
							<CNRRangeForm
								cellInfo={cellInfo}
								rangeInfo={rangeInfo}
								toggleEditModalVisible={props.toggleEditModalVisible}
								baseRangeType={baseRangeType}
								setVesselIdOfCellToBeUpdated={setVesselIdOfCellToBeUpdated}
								vesselIdOfCellToBeUpdated={vesselIdOfCellToBeUpdated}
							/>
						</ModalPortal>
					) : null}
				</div>
			</div>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const vesselStore = vesselSelectors._getStore(store);
	const vesselSubType = reportSelectors.getCNRVesselSubType(reportStore);
	const vesselsSelected =
		reportSelectors.getCNRRangesVesselsSelected(reportStore);
	const reportType =
		reportSelectors.getCNRRangesReportTypeSelected(reportStore);
	const baseRangeType = reportSelectors.getCNRRangesBaseRangeType(reportStore);
	const miniVessels = vesselSelectors.getMiniVessels(
		vesselSelectors.getVessels(vesselStore)
	);
	const isEditModalVisible =
		reportSelectors.getCNRRangesIsEditModalVisible(reportStore);
	const rangeInfo = reportSelectors?.getCNRRangesRangeInfo(reportStore);
	const pagination = reportSelectors.getCNRRangesPagination(reportStore);
	const jobToUploadCNRRangesCSVFile =
		reportSelectors.jobToUploadCNRRangesCSVFile(reportStore);
	const jobToLoadCNRRangesVessels =
		reportSelectors.jobCNRRangesVesselsLoad(reportStore);
	const jobToLoadVessels = vesselSelectors.jobToLoadVessels(vesselStore);

	return {
		vesselSubType,
		miniVessels,
		vesselsSelected,
		reportType,
		baseRangeType,
		isEditModalVisible,
		rangeInfo,
		pagination,
		jobToUploadCNRRangesCSVFile,
		jobToLoadCNRRangesVessels,
		jobToLoadVessels,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		setVesselSubType(vesselSubType?: string) {
			return dispatch(
				reportActions.document.cnrRangesVesselSubTypeSet(vesselSubType)
			);
		},
		loadVessels() {
			return dispatch(vesselActions.listLoad());
		},
		setVesselsSelected(vesselIds?: string[]) {
			return dispatch(
				reportActions.document.cnrRangesVesselsSelected(vesselIds)
			);
		},
		setReportTypeSelected(vesselId?: string) {
			return dispatch(
				reportActions.document.cnrRangesReportTypeSelected(vesselId)
			);
		},
		setBaseRangeType(baseRangeType: ECNR_BASE_RANGE_TYPE) {
			return dispatch(
				reportActions.document.cnrRangesBaseRangeTypeSet(baseRangeType)
			);
		},
		loadVesselSubTypeSpecificConfig() {
			return dispatch(
				reportActions.commands.cnrRangesVesselSubTypeConfigLoad()
			);
		},
		toggleEditModalVisible(isEditModalVisible?: boolean) {
			return dispatch(
				reportActions.document.cnrRangesIsEditModalVisibleToggle(
					isEditModalVisible
				)
			);
		},
		setRangeInfo(rangeInfo?: ICNRRangeVesselLocalRange) {
			return dispatch(reportActions.document.cnrRangesRangeInfoSet(rangeInfo));
		},

		resetPagination() {
			return dispatch(
				reportActions.document.cnrRangesPaginationSet({
					hasMore: true,
					limit: PAGE_SIZE,
					skip: 0,
				})
			);
		},
		resetTable() {
			return dispatch(reportActions.document.cnrRangesVesselListReset());
		},
		resetColumns() {
			return dispatch(reportActions.document.cnrRangesColumnsSet([]));
		},
		loadCNRRangeVessels(params: ICNRRangesVesselsLoadParams) {
			dispatch(reportActions.commands.cnrRangesVesselsLoad(params));
		},
		resetCNRRangeVesselList() {
			dispatch(reportActions.document.cnrRangesVesselListReset());
		},
		uploadCSVOfCNRRanges(file: File) {
			dispatch(reportActions.commands.cnrRangesCSVFileUpload(file));
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(CNRRanges);
